import { Button, Select } from '@lmc/ui-kit'
import { Tooltip } from 'antd'
import useHomeType from 'hooks/useHomeType'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { RoleInfoCard } from '../../generic/RoleInfoCard'
import { useRoleOptions } from '../../generic/teamCapabilities'
import { roleHasVisibleCapabilities } from './capabilityToggles'
import type { WizardData, WizardRole, WizardScope } from './types'

export type Step2RoleProps = {
  value: WizardData
  onChange: (next: WizardData) => void
  onBack: () => void
  onNext: () => void
  onSendInvite: () => void
  isSubmitting?: boolean
  scope?: WizardScope
  testId?: string
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 16px;
`

const Label = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 4px;
`

const LabelTitle = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const LabelHint = styled.div`
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const RedAsterisk = styled.span`
  color: ${({ theme }) => theme.color.status.danger};
  margin-left: 4px;
`

const Footer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.color.neutral.color8};
`

export const Step2Role = ({
  value,
  onChange,
  onBack,
  onNext,
  onSendInvite,
  isSubmitting,
  scope = 'single-site',
  testId
}: Step2RoleProps) => {
  const { t } = useTranslation(NAMESPACE.TEAM)

  const roleOptions = useRoleOptions(scope)
  // CM-4109: with Medication Management hidden for non-eMAR services, Carer has
  // nothing left on step 3 — skip straight to Send invite rather than showing an
  // empty step. The invite payload still carries the role's toggle defaults.
  const { isEmar } = useHomeType()

  const skipStep3 = !roleHasVisibleCapabilities(value.role, { isEmar })

  return (
    <Body data-testid={testId}>
      <div>
        <Label>
          <LabelTitle>
            {t(reportsConstants.WIZARD_STEP2_ROLE)}
            <RedAsterisk aria-hidden="true">*</RedAsterisk>
          </LabelTitle>
          <LabelHint>{t(reportsConstants.WIZARD_STEP2_ROLE_HINT)}</LabelHint>
        </Label>
        <Select
          value={value.role}
          onChange={(role: WizardRole) => onChange({ ...value, role })}
          style={{ width: '100%' }}
          options={roleOptions.map((o) => ({
            value: o.value,
            label: o.tooltip ? (
              <Tooltip title={o.tooltip}>
                <span>{o.label}</span>
              </Tooltip>
            ) : (
              o.label
            ),
            disabled: o.disabled
          }))}
          data-testid={testId ? `${testId}-role-select` : undefined}
        />
      </div>
      <RoleInfoCard role={value.role} />
      <Footer>
        <Button onClick={onBack}>{t(reportsConstants.WIZARD_BACK)}</Button>
        <Button
          variant="primary"
          onClick={skipStep3 ? onSendInvite : onNext}
          isLoading={skipStep3 ? isSubmitting : undefined}
          disabled={skipStep3 ? isSubmitting : undefined}
        >
          {skipStep3
            ? t(reportsConstants.WIZARD_SEND_INVITE)
            : t(reportsConstants.WIZARD_NEXT)}
        </Button>
      </Footer>
    </Body>
  )
}
